import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, CalendarHeart, ChevronRight, Coins, FileText, NotebookTabs, UserRoundCheck, Users } from 'lucide-react';
import MainLayout from '../../../app/layouts/MainLayout';
import { Card } from '../../../components/ui/Card';
import { cn } from '../../../components/ui/Button';
import { useTrimesters } from '../../trimesters/context/TrimestersContext';
import { classroomService } from '../../classes/services/classroom.service';
import { classStudentService } from '../../classes/services/classStudent.service';
import { reportDefinitions } from '../../reports/services/report.service';
import { ClassStudentRecord } from '../../classes/mocks/classStudents';

const reportIconMap = {
  weekly: BarChart3,
  monthly: FileText,
  'class-diary': NotebookTabs,
  'offerings-period': Coins,
} as const;

const DashboardPage = () => {
  const navigate = useNavigate();
  const { currentTrimester, currentTrimesterId } = useTrimesters();
  const [classesCount, setClassesCount] = useState(0);
  const [students, setStudents] = useState<ClassStudentRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!currentTrimesterId) {
        setClassesCount(0);
        setStudents([]);
        return;
      }

      setIsLoading(true);
      try {
        const classes = await classroomService.list(currentTrimesterId);
        setClassesCount(classes.length);

        const studentsByClass = await Promise.all(
          classes.map((classItem) => classStudentService.listByClass(classItem.id, currentTrimesterId))
        );

        const dedup = new Map<string, ClassStudentRecord>();
        studentsByClass.flat().forEach((student) => dedup.set(student.id, student));
        setStudents(Array.from(dedup.values()));
      } catch (error) {
        console.error('Erro ao carregar dashboard', error);
        setClassesCount(0);
        setStudents([]);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [currentTrimesterId]);

  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const dashboardMetrics = useMemo(() => {
    const activeStudents = students.filter((student) => student.currentClassId !== null);
    const birthdaysInMonth = activeStudents.filter((student) => {
      if (!student.birthDate) return false;
      const birthDate = new Date(`${student.birthDate}T00:00:00`);
      return birthDate.getMonth() === today.getMonth();
    });

    return [
      {
        label: 'Alunos',
        value: String(activeStudents.length),
        icon: UserRoundCheck,
        cardTone: 'border-slate-200 bg-white',
        tone: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
        labelTone: 'text-emerald-700',
        valueTone: 'text-emerald-950',
      },
      {
        label: 'Turmas',
        value: String(classesCount),
        icon: Users,
        cardTone: 'border-slate-200 bg-white',
        tone: 'bg-sky-100 text-sky-700 ring-sky-200',
        labelTone: 'text-sky-700',
        valueTone: 'text-sky-950',
      },
      {
        label: 'Aniversariantes (mês)',
        value: String(birthdaysInMonth.length),
        icon: CalendarHeart,
        cardTone: 'border-slate-200 bg-white',
        tone: 'bg-rose-100 text-rose-700 ring-rose-200',
        labelTone: 'text-rose-700',
        valueTone: 'text-rose-950',
      },
    ];
  }, [classesCount, students, today]);

  const studentsWithBirthdays = useMemo(() => {
    const currentYear = today.getFullYear();

    return students
      .filter((student) => student.currentClassId && student.birthDate)
      .map((student) => {
        const birthDate = new Date(`${student.birthDate}T00:00:00`);
        const birthdayThisYear = new Date(currentYear, birthDate.getMonth(), birthDate.getDate());

        return {
          id: student.id,
          name: student.name,
          className: student.classHistory[0]?.className ?? 'Turma',
          birthDate,
          birthdayThisYear,
        };
      });
  }, [students, today]);

  const lastWeekBirthdayStudents = useMemo(() => {
    const lastWeekStart = new Date(today);
    lastWeekStart.setDate(today.getDate() - 7);

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    return studentsWithBirthdays
      .filter((student) => student.birthdayThisYear >= lastWeekStart && student.birthdayThisYear <= yesterday)
      .sort((left, right) => left.birthdayThisYear.getTime() - right.birthdayThisYear.getTime());
  }, [studentsWithBirthdays, today]);

  const nextWeekBirthdayStudents = useMemo(() => {
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const nextWeekEnd = new Date(today);
    nextWeekEnd.setDate(today.getDate() + 7);

    return studentsWithBirthdays
      .filter((student) => student.birthdayThisYear >= tomorrow && student.birthdayThisYear <= nextWeekEnd)
      .sort((left, right) => left.birthdayThisYear.getTime() - right.birthdayThisYear.getTime());
  }, [studentsWithBirthdays, today]);

  const formatBirthdayDayAndMonth = (value: Date) =>
    new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
    }).format(value);

  const renderBirthdayList = (
    items: Array<{
      id: string;
      name: string;
      className: string;
      birthdayThisYear: Date;
    }>,
    emptyMessage: string
  ) => {
    if (items.length === 0) {
      return (
        <div className="rounded-2xl border border-dashed border-rose-200 bg-white/70 px-4 py-6 text-center text-sm text-slate-500">
          {emptyMessage}
        </div>
      );
    }

    return items.map((student) => (
      <div
        key={student.id}
        className="flex items-center justify-between gap-3 rounded-2xl border border-white/80 bg-white/90 px-4 py-3 shadow-sm"
      >
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">{student.name}</p>
          <p className="truncate text-xs text-slate-500">{student.className}</p>
        </div>
        <div className="rounded-2xl bg-rose-50 px-3 py-2 text-center text-rose-700 ring-1 ring-rose-100">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em]">Data</p>
          <p className="text-lg font-bold leading-none">{formatBirthdayDayAndMonth(student.birthdayThisYear)}</p>
        </div>
      </div>
    ));
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <section>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold leading-tight text-slate-900">Bem-vindo, Secretário</h1>
            <p className="text-sm font-medium text-slate-500">{currentTrimester.label || 'Selecione um trimestre para ver os indicadores.'}</p>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-slate-800">Relatórios</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {reportDefinitions.map((report) => {
              const Icon = reportIconMap[report.id as keyof typeof reportIconMap] ?? BarChart3;

              return (
                <button
                  key={report.id}
                  type="button"
                  onClick={() => navigate(`/relatorios/${report.id}?trimester_id=${currentTrimesterId}`)}
                  className="flex w-full items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-4 text-left shadow-sm transition-all hover:bg-slate-50"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="rounded-xl bg-slate-100 p-2 text-slate-700">
                      <Icon size={20} />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-base font-semibold text-slate-900">{report.title}</p>
                      <p className="truncate text-xs text-slate-500">{report.description}</p>
                    </div>
                  </div>
                  <ChevronRight size={18} className="shrink-0 text-slate-400" />
                </button>
              );
            })}
          </div>
        </section>

        <section className="space-y-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {dashboardMetrics.map((metric) => (
              <Card key={metric.label} className={cn('overflow-hidden p-4 shadow-sm', metric.cardTone)}>
                <div className="flex items-center gap-3">
                  <div className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ring-1', metric.tone)}>
                    <metric.icon size={20} />
                  </div>
                  <div className="min-w-0 space-y-2">
                    <span className={cn('block text-[11px] font-semibold uppercase tracking-[0.14em]', metric.labelTone)}>{metric.label}</span>
                    <p className={cn('text-3xl font-bold leading-none', metric.valueTone)}>{metric.value}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          {isLoading && <p className="text-sm text-slate-500">Carregando dados do dashboard...</p>}
        </section>

        <section className="grid gap-3 md:grid-cols-2">
          <Card className="overflow-hidden border-slate-200 bg-white p-5 shadow-sm">
            <div className="space-y-1">
              <div className="inline-flex items-center gap-2 rounded-full bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600 ring-1 ring-slate-200">
                <CalendarHeart size={14} />
                Aniversariantes da semana passada
              </div>
            </div>
            <div className="mt-4 space-y-3">
              {renderBirthdayList(lastWeekBirthdayStudents, 'Nenhum aniversariante na semana passada.')}
            </div>
          </Card>

          <Card className="overflow-hidden border-slate-200 bg-white p-5 shadow-sm">
            <div className="space-y-1">
              <div className="inline-flex items-center gap-2 rounded-full bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600 ring-1 ring-slate-200">
                <CalendarHeart size={14} />
                Aniversariantes da próxima semana
              </div>
            </div>
            <div className="mt-4 space-y-3">
              {renderBirthdayList(nextWeekBirthdayStudents, 'Nenhum aniversariante na próxima semana.')}
            </div>
          </Card>
        </section>
      </div>
    </MainLayout>
  );
};

export default DashboardPage;
